<template>
    <div data-test-view="reviews-automations-edit">
        <div id="modal-body" class="modal-body">
            <form>
                <h4 class="color-dark">
                    {{ $t(`reviews.updateAutomation.titleName`) }}
                </h4>
                <l-input-text
                    v-model="reviewAutomation.name"
                    type="text"
                    :label="``"
                    placeholder="e.g. Probation period assessment"
                    hide-help-block
                />
                <hr />

                <h4 class="color-dark">
                    {{ $t(`reviews.updateAutomation.titleTiming`) }}
                </h4>
                <p>
                    {{ $t(`reviews.updateAutomation.helpTiming`) }}
                </p>
                <div class="row">
                    <div class="col-md-12">
                        <l-select
                            v-model="reviewAutomation.timing.mode"
                            :options="optionsTimingMode"
                            @change="onModeChange"
                        />
                    </div>
                </div>
                <l-space :size="2" />
                <div class="row">
                    <div
                        v-if="reviewAutomation.timing.mode !== 'repeating_with_defined_date'"
                        class="col-md-6"
                    >
                        <l-input-text
                            v-model="reviewAutomation.inclusionTrigger.weeksAfterStart"
                            type="number"
                            :label="
                                $t(`reviews.updateAutomation.labelDaysAfterStart`, {
                                    weeksAfterStart:
                                        reviewAutomation.inclusionTrigger.weeksAfterStart,
                                })
                            "
                            hide-help-block
                        />
                    </div>
                    <!-- Repeating -->
                    <div
                        v-if="reviewAutomation.timing.mode === 'repeating_with_defined_date'"
                        class="col-md-6"
                    >
                        <l-date-picker
                            :disabled-dates="{
                                to: $moment()
                                    .add(-1, 'days')
                                    .toDate(),
                            }"
                            :label="$t(`reviews.updateAutomation.nextTimeOn`)"
                            :value="new Date(reviewAutomation.timing.nextRun)"
                            :on-date-change="
                                date => {
                                    if (date) {
                                        reviewAutomation.timing.nextRun = new Date(date);
                                    }
                                }
                            "
                            hide-help-block
                        />
                    </div>
                    <!-- /Repeating -->
                    <div
                        v-show="
                            ['repeating_with_defined_date', 'repeating'].includes(
                                reviewAutomation.timing.mode,
                            )
                        "
                        class="col-md-6"
                    >
                        <l-input-text
                            v-model="reviewAutomation.timing.repeatsEveryWeeks"
                            type="number"
                            :label="
                                $t(`reviews.updateAutomation.labelRepeatsEvery`, {
                                    repeatsEveryWeeks: reviewAutomation.timing.repeatsEveryWeeks,
                                })
                            "
                            hide-help-block
                        />
                    </div>
                </div>
                <hr />

                <h4 class="color-dark">
                    {{ $t(`reviews.updateAutomation.titleInclusionTrigger`) }}
                </h4>
                <div class="row">
                    <div class="col-md-12">
                        <l-select
                            v-model="reviewAutomation.inclusionTrigger.mode"
                            :options="optionsInclusionTriggerMode"
                        />
                    </div>
                </div>
                <div
                    v-show="
                        reviewAutomation.inclusionTrigger.mode === 'specific' ||
                            reviewAutomation.inclusionTrigger.mode === 'excludeSpecific'
                    "
                >
                    <l-input-typeahead
                        :label="``"
                        :placeholder="$t(`reviews.updateAutomation.labelSelectTeam`)"
                        :show-picture="false"
                        :data="localTeams"
                        :on-select="typeaheadLocalTeamOnSelect"
                        :on-input-change="typeaheadLocalTeamOnInputChange"
                    />
                    <span
                        v-for="(team, index) in reviewAutomation.inclusionTrigger.specificTeams"
                        v-show="reviewAutomation.inclusionTrigger.specificTeams.length"
                        :key="team._id"
                    >
                        <span
                            class="label label-default clickable r-default-margin-s"
                            tabindex="0"
                            @click="removeTeam(index)"
                            @keyup.enter="removeTeam(index)"
                        >
                            {{ team.name }} <i class="fa fa-remove"></i>
                        </span>
                    </span>
                </div>
                <hr />

                <h4 class="color-dark">
                    {{ $t(`reviews.updateAutomation.titleTemplate`) }}
                </h4>
                <p>
                    {{ $t(`reviews.updateAutomation.helpTemplate`) }}
                </p>
                <div class="row">
                    <div class="col-md-12">
                        <l-select v-model="reviewAutomation.template" :options="optionsTemplate" />
                    </div>
                </div>

                <div v-show="!reviewAutomation.isNew">
                    <hr />
                    <h4 class="color-dark">
                        {{ $t(`reviews.updateAutomation.titlePreview`) }}
                    </h4>
                    <div v-if="automationPreview.length">
                        <p><l-text :text="$t(`reviews.updateAutomation.helpPreview`)" /></p>
                        <br />
                        <div v-for="participant in automationPreview" :key="participant._id">
                            <div class="row">
                                <div class="col-md-7">
                                    <strong>{{ participant.fullname }}</strong>
                                </div>
                                <div
                                    v-if="
                                        reviewAutomation.timing.mode !==
                                            'repeating_with_defined_date'
                                    "
                                    class="col-md-5"
                                >
                                    <span class="r-default-margin-s">{{
                                        $t(`reviews.updateAutomation.labelStartedOn`)
                                    }}</span>
                                    {{ participant.startDate | moment("MMM DD, YYYY") }}
                                </div>
                            </div>
                            <div class="hr-small"></div>
                        </div>
                    </div>
                    <div v-else>
                        <l-no-data :text="$t(`reviews.updateAutomation.labelPreviewEmpty`)" />
                    </div>
                </div>

                <div v-if="error">
                    <l-space :size="2" />
                    <l-text-alert type="danger" :text="error.message" />
                </div>
            </form>
        </div>

        <l-space :size="1" />

        <div class="footer">
            <button class="btn btn-primary" type="button" @click="cancel()">
                {{ $t(`reviews.updateAutomation.btnCancel`) }}
            </button>
            <button class="btn btn-primary" type="button" @click="updatePreview()">
                {{ $t(`reviews.updateAutomation.btnSavePreview`) }}
            </button>
            <button class="btn btn-primary" type="button" @click="updateAutomation()">
                {{ $t(`reviews.updateAutomation.btnSaveAutomation`) }}
            </button>
        </div>
    </div>
</template>

<script>
import { mapGetters } from "vuex";

import { chronologically } from "@/utils/sort-object";

import LInputText from "@/components/LInputText";
import LDatePicker from "@/components/LDatePicker";
import LInputTypeahead from "@/components/LInputTypeahead";
import LNoData from "@/components/LNoData";
import LSelect from "@/components/LSelect";
import LSpace from "@/components/LSpace";
import LText from "@/components/LText";
import LTextAlert from "@/components/LTextAlert";

export default {
    components: {
        LInputText,
        LDatePicker,
        LInputTypeahead,
        LNoData,
        LSelect,
        LSpace,
        LText,
        LTextAlert,
    },
    props: {
        id: {
            type: String,
            required: true,
        },
    },
    data() {
        return {
            reviewAutomation: {},
            automationPreview: [],
            error: null,
        };
    },
    computed: {
        ...mapGetters({
            getReviewAutomation: "reviews/automations/details",
            getReviewAutomationPreview: "reviews/automations/preview",
            reviewTemplates: "reviews/assessments/templatesActive",
            teamsSearchList: "teams/search/list",
        }),

        localTeams() {
            return this.teamsSearchList.map(team => ({
                _id: team._id,
                name: team.name,
            }));
        },

        optionsTimingMode() {
            return [
                {
                    value: "once",
                    text: this.$t(`reviews.updateAutomation.optionOnce`),
                },
                {
                    value: "repeating",
                    text: this.$t(`reviews.updateAutomation.optionRepeating`),
                },
                {
                    value: "repeating_with_defined_date",
                    text: this.$t(`reviews.updateAutomation.optionRepeatingWithDefinedStart`),
                },
            ];
        },
        optionsInclusionTriggerMode() {
            return [
                {
                    value: "all",
                    text: this.$t(`reviews.updateAutomation.optionAllEmployees`),
                },
                {
                    value: "specific",
                    text: this.$t(`reviews.updateAutomation.optionSpecificTeams`),
                },
                {
                    value: "excludeSpecific",
                    text: this.$t(`reviews.updateAutomation.optionExcludeSpecificTeams`),
                },
            ];
        },
        optionsTemplate() {
            return this.reviewTemplates.map(template => ({
                value: template._id,
                text: template.name,
            }));
        },
    },
    watch: {
        id: {
            immediate: true,
            handler(id) {
                this.reviewAutomation = this.getReviewAutomation(id);

                this.automationPreview = this.getReviewAutomationPreview(this.id).sort(
                    chronologically("startDate"),
                );
            },
        },
    },
    methods: {
        onModeChange({ target }) {
            if (
                target.value == "repeating_with_defined_date" &&
                !this.reviewAutomation.timing.nextRun
            ) {
                this.reviewAutomation.timing.nextRun = this.$moment().add(7, "days");
            }
        },
        typeaheadLocalTeamOnSelect(team) {
            if (!this.reviewAutomation.inclusionTrigger.specificTeams) {
                this.reviewAutomation.inclusionTrigger.specificTeams = [];
            }
            if (
                !this.reviewAutomation.inclusionTrigger.specificTeams.find(p => p._id === team._id)
            ) {
                this.reviewAutomation.inclusionTrigger.specificTeams.push(team);
            }
            this.$store.dispatch("teams/search/reset");
        },
        typeaheadLocalTeamOnInputChange(query) {
            if (query.length >= 2) {
                this.$store.dispatch("teams/search/findByName", { query });
            } else {
                this.$store.dispatch("teams/search/reset");
            }
        },
        removeTeam(index) {
            this.reviewAutomation.inclusionTrigger.specificTeams.splice(index, 1);
        },

        cancel() {
            this.$router.push("/reviews/automations");
        },
        async updatePreview() {
            this.error = null;

            await this.$store
                .dispatch({
                    type: "reviews/automations/update",
                    payload: { automation: this.reviewAutomation },
                })
                .catch(err => {
                    this.error = { message: err?.response?.data?.message };
                });

            if (this.error) {
                return;
            }

            await this.$store.dispatch({
                type: "reviews/automations/getPreview",
                payload: { id: this.id },
            });

            if (this.reviewAutomation.isNew) {
                this.reviewAutomation.isNew = false;
            }

            this.automationPreview = this.getReviewAutomationPreview(this.id).sort(
                chronologically("startDate"),
            );
        },
        async updateAutomation() {
            this.error = null;

            await this.$store
                .dispatch({
                    type: "reviews/automations/update",
                    payload: { automation: this.reviewAutomation },
                })
                .catch(err => {
                    this.error = { message: err?.response?.data?.message };
                });

            if (this.error) {
                return;
            }
            this.$router.push("/reviews/automations");
        },
    },
};
</script>
